<template>
    <Lottie
        class="AnimatedNumbershapeLottie"
        :style="{ '--color': color }"
        v-bind="lottieProps"
        @animCreated="handleAnimation"
    />
</template>

<script>
import Lottie from '@/components/Lottie';
const anims = require.context('./lottie-anims', true, /\.(json)$/);

export default {
    components: { Lottie },
    props: {
        number: {
            type: String,
            required: true
        },
        color: {
            type: String,
            default: 'black'
        },
        reversed: {
            type: Boolean,
            default: false
        },
        play: {
            type: Boolean,
            default: false
        },
        paused: {
            type: Boolean,
            default: false
        },
        holdAtEnd: {
            type: Number,
            default: 1000
        },
        holdAtStart: {
            type: Number,
            default: 1000
        },
        speed: {
            type: Number,
            default: 1
        },
        rewindSpeed: {
            type: Number,
            default: 3
        }
    },
    data() {
        return {
            direction: this.reversed ? -1 : 1,
            pauseTimeoutId: null,
            rewind: true
        };
    },
    computed: {
        lottieProps() {
            const animationData = anims(`./${this.number}.json`);
            return {
                options: {
                    autoplay: false,
                    animationData
                }
            };
        }
    },
    watch: {
        play(flag) {
            if (flag) {
                this.startAnimation();
            } else {
                this.startRewind();
            }
        },
        paused(flag) {
            if (flag) {
                if (this.pauseTimeoutId) {
                    clearTimeout(this.pauseTimeoutId);
                } else {
                    this.anim.pause();
                }
            } else {
                this.anim.play();
            }
        }
    },
    beforeDestroy() {
        clearTimeout(this.pauseTimeoutId);
    },
    methods: {
        setInitialDirectionAndFrame() {
            this.totalFrames = this.anim.totalFrames;
            this.direction = this.reversed ? -1 : 1;

            this.anim.playSegments([0, this.totalFrames + 1 - 0.001], true);
            this.anim.playSegments([], true);

            this.anim.setDirection(this.direction);
            this.anim.setSpeed(this.speed);

            if (this.direction === -1) {
                this.anim.goToAndStop(this.totalFrames - 0.001, true);
            } else {
                this.anim.goToAndStop(0);
            }
        },
        startAnimation() {
            this.anim.setSpeed(this.speed);
            this.anim.play();
        },
        startRewind() {
            this.anim.setSpeed(this.rewindSpeed);

            if (this.pauseTimeoutId) {
                if ((this.reversed ? 1 : -1) === this.direction) {
                    this.anim.play();
                }
            } else {
                clearTimeout(this.pauseTimeoutId);
                this.pauseTimeoutId = null;

                if ((this.reversed ? -1 : 1) === this.direction) {
                    this.direction = this.direction * -1;
                    this.anim.setDirection(this.direction);
                }
            }
        },
        startHold() {
            this.pauseTimeoutId = setTimeout(
                () => {
                    this.pauseTimeoutId = null;
                    this.onHoldCompleted();
                },
                this.direction === 1 ? this.holdAtEnd : this.holdAtStart
            );
        },
        onLottieAnimationCompleted() {
            this.direction = this.direction * -1;
            this.anim.setDirection(this.direction);
            if (!this.play) {
                return;
            }
            this.startHold();
        },
        onHoldCompleted() {
            if (!this.play) {
                return;
            }
            this.anim.play();
        },
        handleAnimation(anim) {
            this.anim = anim;

            this.setInitialDirectionAndFrame();

            anim.addEventListener('complete', this.onLottieAnimationCompleted);

            if (this.play) {
                this.startAnimation();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.AnimatedNumbershapeLottie {
    /deep/ svg {
        * {
            stroke: var(--color) !important;
            fill: var(--color) !important;
        }
    }
    /deep/ svg #guides {
        opacity: 0;
        visibility: hidden;
    }
}
</style>
